import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { MY_LINKEDIN, SAVE_LINKEDIN, ME } from '../graphql';
import { Save, Copy, Check, Linkedin, ChevronRight, ChevronLeft, Sparkles, AlertCircle } from 'lucide-react';

const DEFAULT_LI = {
  headline: '',
  summary: '',
  location: '',
  industry: '',
  skills: [],
  skillInput: '',
  education: [{ school: '', degree: '', field: '', from: '', to: '', grade: '', activities: '' }],
  experience: [{ company: '', title: '', from: '', to: '', current: false, description: '' }],
  projects: [{ name: '', url: '', description: '' }],
  certifications: [{ name: '', issuer: '', date: '' }],
  languages: [{ language: '', proficiency: 'Professional Working' }],
  volunteer: '',
  openTo: [],
};

const STEPS = [
  { id: 'headline',  label: 'Headline & Industry' },
  { id: 'summary',   label: 'About / Summary' },
  { id: 'education', label: 'Education' },
  { id: 'experience',label: 'Experience' },
  { id: 'projects',  label: 'Projects & Work' },
  { id: 'skills',    label: 'Skills' },
  { id: 'certs',     label: 'Certifications' },
  { id: 'extras',    label: 'Extras & Open To' },
  { id: 'review',    label: 'Review & Copy' },
];

const OPEN_TO_OPTIONS = ['Full-time roles', 'Internships', 'Freelance / Contract', 'Mentorship (giving)', 'Collaboration on projects', 'Speaking opportunities'];
const PROFICIENCIES = ['Elementary', 'Limited Working', 'Professional Working', 'Full Professional', 'Native/Bilingual'];

function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="flex items-center gap-1 text-xs text-brand-600 hover:underline">
      {copied ? <Check size={12} className="text-green-500"/> : <Copy size={12}/>}
      {copied ? 'Copied!' : (label || 'Copy')}
    </button>
  );
}

function StrengthMeter({ data }) {
  const checks = [
    { label: 'Headline', done: data.headline?.length > 10 },
    { label: 'About', done: data.summary?.length > 80 },
    { label: 'Education', done: data.education?.some(e=>e.school) },
    { label: 'Experience / Projects', done: data.experience?.some(e=>e.company) || data.projects?.some(p=>p.name) },
    { label: '5+ Skills', done: (data.skills||[]).length >= 5 },
    { label: 'Certification', done: data.certifications?.some(c=>c.name) },
    { label: 'Open To', done: (data.openTo||[]).length > 0 },
    { label: 'Location set', done: data.location?.length > 0 },
  ];
  const done = checks.filter(c=>c.done).length;
  const pct = Math.round(done / checks.length * 100);
  const level = pct < 40 ? { label: 'Beginner', color: 'bg-red-400' } : pct < 70 ? { label: 'Intermediate', color: 'bg-amber-400' } : pct < 90 ? { label: 'Advanced', color: 'bg-blue-500' } : { label: 'All-Star', color: 'bg-green-500' };

  return (
    <div className="card !p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-700">Profile Strength</span>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${level.color}`}>{level.label}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full mb-3">
        <div className={`h-2 rounded-full transition-all duration-500 ${level.color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-2 gap-1">
        {checks.map(c => (
          <div key={c.label} className={`text-xs flex items-center gap-1 ${c.done ? 'text-green-600' : 'text-gray-400'}`}>
            {c.done ? '✓' : '○'} {c.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, multiline, hint, type='text' }) {
  const cls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-400 transition-colors";
  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label}{hint && <span className="text-gray-400 font-normal ml-1">— {hint}</span>}</label>}
      {multiline
        ? <textarea className={`${cls} resize-none h-28`} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}/>
        : <input className={cls} type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}/>}
    </div>
  );
}

export default function LinkedInPage() {
  const { data: liData } = useQuery(MY_LINKEDIN);
  const { data: meData } = useQuery(ME);
  const [save, { loading: saving }] = useMutation(SAVE_LINKEDIN, { refetchQueries: [MY_LINKEDIN] });
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [d, setD] = useState(DEFAULT_LI);
  const [saved, setSaved] = useState(false);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (liData?.myLinkedInProfile?.data) {
      try { setD(JSON.parse(liData.myLinkedInProfile.data)); } catch(_) {}
    } else if (meData?.me) {
      const u = meData.me;
      setD(prev => ({ ...prev, location: u.college || '' }));
    }
  }, [liData, meData]);

  const up = (key, val) => { setD(prev => ({ ...prev, [key]: val })); setSaved(false); };
  const upArr = (key, i, field, val) => {
    const arr = [...(d[key]||[])];
    arr[i] = { ...arr[i], [field]: val };
    up(key, arr);
  };

  const handleSave = async () => {
    await save({ variables: { data: JSON.stringify(d) } });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const addSkill = () => {
    const s = d.skillInput?.trim();
    if (s && !d.skills.includes(s)) { up('skills', [...d.skills, s]); up('skillInput', ''); }
  };

  const currentStep = STEPS[step];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-xl"><Linkedin size={20} className="text-blue-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">LinkedIn Profile Builder</h1>
            <p className="text-gray-500 text-sm">Step-by-step builder with optimisation tips for each section</p>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-secondary flex items-center gap-1.5 text-sm">
          <Save size={14}/>{saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Progress'}
        </button>
      </div>

      <div className="flex gap-5">
        {/* ── LEFT: STEPS ── */}
        <div className="w-52 flex-shrink-0 space-y-1">
          {STEPS.map((s, i) => (
            <button key={s.id} onClick={() => setStep(i)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${step===i ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50'}`}>
              <span className={`text-xs font-semibold w-4 text-center ${step===i ? 'text-blue-200' : 'text-gray-400'}`}>{i+1}</span>
              <span className="flex-1">{s.label}</span>
              {i < step && <Check size={12} className={step===i ? 'text-blue-200' : 'text-green-500'}/>}
            </button>
          ))}
          <div className="pt-3">
            <StrengthMeter data={d} />
          </div>
        </div>

        {/* ── RIGHT: STEP CONTENT ── */}
        <div className="flex-1 card !p-6 space-y-5">

          {/* STEP: Headline */}
          {currentStep.id === 'headline' && (
            <>
              <StepHeader title="Headline & Location" subtitle="Your headline is the first thing recruiters see — make it count" />
              <Field label="Headline" value={d.headline} onChange={v=>up('headline',v)} placeholder="Final Year CSE @ IIT Delhi | SDE Intern @Amazon | Full-Stack & System Design" hint="Max 220 chars" />
              <div className="bg-blue-50 rounded-xl p-4 space-y-1">
                <p className="text-xs font-semibold text-blue-700">Headline Formulas</p>
                <p className="text-xs text-blue-600">• [Year] [Degree] @ [College] | [Role/Skill] | [Aspiration]</p>
                <p className="text-xs text-blue-600">• [Role] @ [Company] | Passionate about [domain]</p>
                <p className="text-xs text-blue-600">• Building [thing] with [tech] | [School] '25</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Location" value={d.location} onChange={v=>up('location',v)} placeholder="Bengaluru, Karnataka, India" />
                <Field label="Industry" value={d.industry} onChange={v=>up('industry',v)} placeholder="Computer Software" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Open To</label>
                <div className="flex flex-wrap gap-2">
                  {OPEN_TO_OPTIONS.map(opt => (
                    <button key={opt} onClick={() => {
                      const ot = d.openTo?.includes(opt) ? d.openTo.filter(x=>x!==opt) : [...(d.openTo||[]), opt];
                      up('openTo', ot);
                    }} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${d.openTo?.includes(opt) ? 'bg-blue-600 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-blue-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* STEP: Summary */}
          {currentStep.id === 'summary' && (
            <>
              <StepHeader title="About / Summary" subtitle="2–3 paragraphs that tell your story. Recruiters spend ~8 seconds here." />
              <Field multiline label="Your Summary (write in first person)" value={d.summary} onChange={v=>up('summary',v)} placeholder={`I'm a final-year Computer Science student at XYZ University with a strong foundation in data structures, algorithms, and full-stack development.\n\nI've interned at [company] where I [achievement]. I'm passionate about building [domain] products that [impact].\n\nCurrently seeking [role] opportunities where I can contribute to scalable, impactful systems.`} />
              <CharCount value={d.summary} max={2600} />
              <div className="grid grid-cols-1 gap-3 bg-amber-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-amber-700">What to include (proven formula)</p>
                <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Who you are (student/role) + college</li>
                  <li>Top 2-3 technical strengths</li>
                  <li>Best achievement / internship impact</li>
                  <li>What you're building / working on now</li>
                  <li>What you're seeking (role/type)</li>
                </ol>
              </div>
              {d.summary && (
                <div className="flex justify-end">
                  <CopyBtn text={d.summary} label="Copy summary to clipboard" />
                </div>
              )}
            </>
          )}

          {/* STEP: Education */}
          {currentStep.id === 'education' && (
            <>
              <StepHeader title="Education" subtitle="Add your degree(s) — including 10th & 12th if relevant" />
              {d.education.map((edu, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-400">#{i+1}</span>
                    {d.education.length > 1 && <button onClick={() => up('education', d.education.filter((_,j)=>j!==i))} className="text-xs text-red-400 hover:underline">Remove</button>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="School/University" value={edu.school} onChange={v=>upArr('education',i,'school',v)} placeholder="IIT Delhi" />
                    <Field label="Degree" value={edu.degree} onChange={v=>upArr('education',i,'degree',v)} placeholder="Bachelor of Technology" />
                    <Field label="Field of Study" value={edu.field} onChange={v=>upArr('education',i,'field',v)} placeholder="Computer Science and Engineering" />
                    <Field label="Grade / CGPA" value={edu.grade} onChange={v=>upArr('education',i,'grade',v)} placeholder="8.4/10" />
                    <Field label="From (year)" value={edu.from} onChange={v=>upArr('education',i,'from',v)} placeholder="2021" />
                    <Field label="To (year)" value={edu.to} onChange={v=>upArr('education',i,'to',v)} placeholder="2025" />
                  </div>
                  <Field label="Activities / Societies (optional)" value={edu.activities||''} onChange={v=>upArr('education',i,'activities',v)} placeholder="ACM Student Chapter, Coding Club Coordinator, National Football Team" />
                </div>
              ))}
              <button onClick={() => up('education', [...d.education, { school:'',degree:'',field:'',from:'',to:'',grade:'',activities:'' }])} className="text-sm text-blue-600 hover:underline">+ Add another education</button>
            </>
          )}

          {/* STEP: Experience */}
          {currentStep.id === 'experience' && (
            <>
              <StepHeader title="Experience" subtitle="Internships, part-time, and significant on-campus roles" />
              {d.experience.map((exp, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-xs font-bold text-gray-400">Experience #{i+1}</span>
                    {d.experience.length > 1 && <button onClick={() => up('experience', d.experience.filter((_,j)=>j!==i))} className="text-xs text-red-400 hover:underline">Remove</button>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Company" value={exp.company} onChange={v=>upArr('experience',i,'company',v)} placeholder="Google" />
                    <Field label="Title" value={exp.title} onChange={v=>upArr('experience',i,'title',v)} placeholder="Software Engineering Intern" />
                    <Field label="From (Mon YYYY)" value={exp.from} onChange={v=>upArr('experience',i,'from',v)} placeholder="May 2024" />
                    <Field label="To (Mon YYYY / Present)" value={exp.to} onChange={v=>upArr('experience',i,'to',v)} placeholder="Jul 2024" />
                  </div>
                  <Field multiline label="Description (use bullet-style, start with action verbs)" value={exp.description} onChange={v=>upArr('experience',i,'description',v)} placeholder="• Built a recommendation engine using collaborative filtering that improved CTR by 18%&#10;• Reduced API response time by 35% by optimising N+1 queries&#10;• Collaborated with 4 cross-functional teams across 3 time zones" />
                </div>
              ))}
              <button onClick={() => up('experience', [...d.experience, { company:'',title:'',from:'',to:'',current:false,description:'' }])} className="text-sm text-blue-600 hover:underline">+ Add experience</button>
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-blue-700 mb-1">Action verb starters</p>
                <p className="text-xs text-blue-600">Built · Designed · Optimised · Reduced · Increased · Led · Collaborated · Shipped · Integrated · Automated · Improved · Implemented</p>
              </div>
            </>
          )}

          {/* STEP: Projects */}
          {currentStep.id === 'projects' && (
            <>
              <StepHeader title="Projects" subtitle="Showcase personal/academic projects — add GitHub links for credibility" />
              {d.projects.map((proj, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-xs font-bold text-gray-400">Project #{i+1}</span>
                    {d.projects.length > 1 && <button onClick={() => up('projects', d.projects.filter((_,j)=>j!==i))} className="text-xs text-red-400 hover:underline">Remove</button>}
                  </div>
                  <Field label="Project Name" value={proj.name} onChange={v=>upArr('projects',i,'name',v)} placeholder="aura — College Super App" />
                  <Field label="GitHub / Live URL" value={proj.url} onChange={v=>upArr('projects',i,'url',v)} placeholder="https://github.com/yourname/aura" />
                  <Field multiline label="Description" value={proj.description} onChange={v=>upArr('projects',i,'description',v)} placeholder="Built a full-stack college management app using React, GraphQL, and Node.js serving 500+ students. Implemented real-time notifications, attendance tracking, and placement prep module." />
                </div>
              ))}
              <button onClick={() => up('projects', [...d.projects, { name:'',url:'',description:'' }])} className="text-sm text-blue-600 hover:underline">+ Add project</button>
            </>
          )}

          {/* STEP: Skills */}
          {currentStep.id === 'skills' && (
            <>
              <StepHeader title="Skills" subtitle="Add 15–50 skills. LinkedIn's algo boosts profiles with relevant skills." />
              <div className="flex gap-2">
                <input className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" value={d.skillInput||''} onChange={e=>up('skillInput',e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();addSkill();}}} placeholder="Type a skill and press Enter — e.g. React, Java, Agile" />
                <button onClick={addSkill} className="btn-primary text-sm">Add</button>
              </div>
              {d.skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {d.skills.map(s => (
                    <span key={s} className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      {s}
                      <button onClick={() => up('skills', d.skills.filter(x=>x!==s))} className="text-blue-300 hover:text-red-400 ml-1">×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-amber-700 mb-2">Top skills recruiters search</p>
                <div className="flex flex-wrap gap-1">
                  {['Python','Java','C++','JavaScript','React','Node.js','SQL','Machine Learning','Data Structures','System Design','Docker','AWS','Git','REST APIs','GraphQL','Problem Solving','Leadership','Communication','Agile'].map(skill => (
                    <button key={skill} onClick={() => { if(!d.skills.includes(skill)) up('skills', [...d.skills, skill]); }} className={`px-2 py-1 rounded-full text-xs border transition-colors ${d.skills.includes(skill) ? 'bg-blue-600 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-blue-300'}`}>
                      {skill}
                    </button>
                  ))}
                </div>
              </div>
              {/* Languages */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Languages Known</label>
                {d.languages.map((lang, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400" value={lang.language} onChange={e=>upArr('languages',i,'language',e.target.value)} placeholder="English, Hindi, Tamil…" />
                    <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400" value={lang.proficiency} onChange={e=>upArr('languages',i,'proficiency',e.target.value)}>
                      {PROFICIENCIES.map(p=><option key={p}>{p}</option>)}
                    </select>
                    {d.languages.length > 1 && <button onClick={() => up('languages', d.languages.filter((_,j)=>j!==i))} className="text-gray-300 hover:text-red-400 text-xs">✕</button>}
                  </div>
                ))}
                <button onClick={() => up('languages', [...d.languages, {language:'',proficiency:'Professional Working'}])} className="text-xs text-blue-600 hover:underline">+ Add language</button>
              </div>
            </>
          )}

          {/* STEP: Certifications */}
          {currentStep.id === 'certs' && (
            <>
              <StepHeader title="Certifications & Licenses" subtitle="Verified credentials — include the issuing date so LinkedIn shows the badge" />
              {d.certifications.map((cert, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Certification Name" value={cert.name} onChange={v=>upArr('certifications',i,'name',v)} placeholder="AWS Cloud Practitioner" />
                    <Field label="Issuing Organization" value={cert.issuer} onChange={v=>upArr('certifications',i,'issuer',v)} placeholder="Amazon Web Services" />
                    <Field label="Issue Date (Mon YYYY)" value={cert.date} onChange={v=>upArr('certifications',i,'date',v)} placeholder="Jun 2024" />
                  </div>
                  {d.certifications.length > 1 && <button onClick={() => up('certifications', d.certifications.filter((_,j)=>j!==i))} className="text-xs text-red-400 hover:underline">Remove</button>}
                </div>
              ))}
              <button onClick={() => up('certifications', [...d.certifications, {name:'',issuer:'',date:''}])} className="text-sm text-blue-600 hover:underline">+ Add certification</button>
            </>
          )}

          {/* STEP: Extras */}
          {currentStep.id === 'extras' && (
            <>
              <StepHeader title="Extras & Volunteer Work" subtitle="Volunteering, causes, and other interests boost profile authenticity" />
              <Field multiline label="Volunteering / Causes (optional)" value={d.volunteer||''} onChange={v=>up('volunteer',v)} placeholder="NSS Volunteer — organized blood donation camp for 300+ participants&#10;Teaching Assistant — taught Python to underprivileged students via NGO" />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">#OpenToWork / Open For</label>
                <div className="flex flex-wrap gap-2">
                  {OPEN_TO_OPTIONS.map(opt => (
                    <button key={opt} onClick={() => {
                      const ot = d.openTo?.includes(opt) ? d.openTo.filter(x=>x!==opt) : [...(d.openTo||[]), opt];
                      up('openTo', ot);
                    }} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${d.openTo?.includes(opt) ? 'bg-blue-600 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-blue-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-green-700 mb-2">Final profile checklist</p>
                <ul className="text-xs text-green-700 space-y-1">
                  <li>• Profile photo: professional, good lighting, clear face</li>
                  <li>• Banner image: college/tech themed (Canva templates work great)</li>
                  <li>• Connect with 50+ people in your college first (seed connections)</li>
                  <li>• Post 1-2 project updates per month — LinkedIn rewards activity</li>
                  <li>• Request 2-3 recommendations from professors/managers</li>
                  <li>• Turn on "Open to Work" in settings (visible to recruiters only option)</li>
                </ul>
              </div>
            </>
          )}

          {/* STEP: Review */}
          {currentStep.id === 'review' && (
            <>
              <StepHeader title="Review & Copy" subtitle="Everything ready — copy each section to paste into LinkedIn" />
              <StrengthMeter data={d} />
              <div className="space-y-4">
                <ReviewCard title="Headline" content={d.headline} />
                <ReviewCard title="About / Summary" content={d.summary} />
                {d.experience?.some(e=>e.company) && (
                  <div className="border border-gray-100 rounded-xl p-4">
                    <p className="text-xs font-bold text-gray-500 mb-3">Experience Descriptions</p>
                    {d.experience.filter(e=>e.company).map((exp,i) => (
                      <div key={i} className="mb-3">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-700">{exp.title} @ {exp.company}</span>
                          <CopyBtn text={exp.description} />
                        </div>
                        <p className="text-xs text-gray-500 whitespace-pre-line">{exp.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                {d.skills?.length > 0 && (
                  <div className="border border-gray-100 rounded-xl p-4">
                    <div className="flex justify-between mb-2">
                      <p className="text-xs font-bold text-gray-500">Skills ({d.skills.length})</p>
                      <CopyBtn text={d.skills.join(', ')} />
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {d.skills.map(s=><span key={s} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{s}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t border-gray-100">
            <button onClick={() => setStep(s => Math.max(0, s-1))} disabled={step===0} className="btn-secondary flex items-center gap-1 text-sm disabled:opacity-40">
              <ChevronLeft size={14}/> Back
            </button>
            <button
              disabled={saving || finished}
              onClick={async () => {
                await handleSave();
                if (step < STEPS.length - 1) {
                  setStep(s => s + 1);
                } else {
                  setFinished(true);
                  setTimeout(() => navigate('/'), 1200);
                }
              }}
              className="btn-primary flex items-center gap-1 text-sm disabled:opacity-60">
              {saving ? 'Saving…' : finished ? <><Check size={14}/> Saved!</> : step === STEPS.length - 1 ? <>Save & Finish <ChevronRight size={14}/></> : <>Save & Next <ChevronRight size={14}/></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepHeader({ title, subtitle }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
    </div>
  );
}

function ReviewCard({ title, content }) {
  if (!content) return null;
  return (
    <div className="border border-gray-100 rounded-xl p-4">
      <div className="flex justify-between items-center mb-2">
        <p className="text-xs font-bold text-gray-500">{title}</p>
        <CopyBtn text={content} />
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-line">{content}</p>
    </div>
  );
}

function CharCount({ value, max }) {
  const len = (value||'').length;
  const color = len > max * 0.9 ? 'text-red-500' : len > max * 0.7 ? 'text-amber-500' : 'text-gray-400';
  return <p className={`text-xs text-right ${color}`}>{len}/{max} characters</p>;
}
